/**
 * 🔧 MODAL SYSTEM DEBUG & TEST UTILITIES
 *
 * This file provides testing utilities for the newly implemented Redux-based modal system.
 * Use these functions to verify modal functionality after fixing the "Invalid Hook Call" error.
 */

import { store } from "@/core/store";
import {
  selectModalStack,
  selectHasModal,
  selectModalById,
  selectIsModalLoading,
  selectModalError,
} from "@/core/modal";

/**
 * Test Redux Modal System - Hook Error Fix Verification
 *
 * TESTING INSTRUCTIONS:
 * ===================
 *
 * 1. BASIC MODAL OPENING (Hook Error Fix Test):
 *    - Click on any product card
 *    - Modal should open WITHOUT "Invalid hook call" error
 *    - Check browser console for errors
 *
 * 2. MODAL REOPENING TEST:
 *    - Open a product modal (click product card)
 *    - Close the modal (click X or overlay)
 *    - Open the SAME product modal again
 *    - Should open without "already exists in stack" error
 *
 * 3. MULTIPLE MODALS TEST:
 *    - Open first product modal
 *    - Open second product modal (different product)
 *    - Both should work with proper z-index stacking
 *
 * 4. REDUX DEVTOOLS VERIFICATION:
 *    - Open Redux DevTools
 *    - Watch modal/pushModal and modal/popModal actions
 *    - Verify state.modal.stack and state.modal.modalStates updates
 *
 * 5. ERROR RECOVERY TEST:
 *    - Try opening invalid modal types
 *    - System should handle gracefully without crashes
 */

/**
 * Debug function to test Redux modal state
 * Usage: window.testReduxModals()
 */
function testReduxModals() {
  console.group("🔧 MODAL SYSTEM DEBUG - Hook Error Fix Verification");

  const state = store.getState();

  console.log("📊 Current Modal State:");
  console.log("  Stack:", selectModalStack(state));
  console.log("  Modal States:", state.modal.modalStates);
  console.log("  Next Z-Index:", state.modal.nextZIndex);

  console.log("\n🧪 Testing Modal Checks:");

  // Test hasModal function
  const testModalId = "product-test123";
  const hasTestModal = selectHasModal(state, testModalId);
  console.log(`  Has modal "${testModalId}":`, hasTestModal);

  // Test modal by ID
  const modalById = selectModalById(state, testModalId);
  console.log(`  Modal by ID "${testModalId}":`, modalById);

  // Test loading state
  const isLoading = selectIsModalLoading(state, testModalId);
  console.log(`  Is modal "${testModalId}" loading:`, isLoading);

  // Test error state
  const modalError = selectModalError(state, testModalId);
  console.log(`  Modal "${testModalId}" error:`, modalError);

  console.log("\n✅ Hook Error Fix Status:");
  console.log("  - No more useAppSelector calls in callbacks");
  console.log("  - Using store.getState() for synchronous access");
  console.log("  - All hooks moved to hook body level");
  console.log("  - Fixed ModalStackItem import issues");

  console.groupEnd();
}

/**
 * Advanced debugging for modal lifecycle
 */
function debugModalLifecycle() {
  console.group("🔄 MODAL LIFECYCLE DEBUG");

  // Subscribe to store changes
  const unsubscribe = store.subscribe(() => {
    const state = store.getState();
    const modalStack = selectModalStack(state);

    console.log("🔄 Modal Stack Updated:", {
      stackSize: modalStack.length,
      modals: modalStack.map((m) => ({
        id: m.id,
        type: m.type,
        zIndex: m.zIndex,
      })),
      timestamp: new Date().toISOString(),
    });
  });

  console.log("📝 Subscribed to modal state changes");
  console.log("💡 Open/close modals to see live updates");
  console.log("🛑 Call window.stopModalDebug() to stop tracking");

  // Store unsubscribe function globally
  (window as any).stopModalDebug = () => {
    unsubscribe();
    console.log("🛑 Modal debug tracking stopped");
    console.groupEnd();
  };
}

/**
 * Test modal error handling
 */
function testModalErrorHandling() {
  console.group("⚠️ MODAL ERROR HANDLING TEST");

  try {
    // These should not crash the app
    console.log("Testing hasModal with invalid ID...");
    const state = store.getState();
    const hasInvalid = selectHasModal(state, "");
    console.log("✅ Empty ID handled:", hasInvalid);

    const hasNull = selectHasModal(state, null as any);
    console.log("✅ Null ID handled:", hasNull);

    console.log("✅ All error scenarios handled gracefully");
  } catch (error) {
    console.error("❌ Error handling failed:", error);
  }

  console.groupEnd();
}

// Make functions available globally for browser console testing
declare global {
  interface Window {
    testReduxModals: typeof testReduxModals;
    debugModalLifecycle: typeof debugModalLifecycle;
    testModalErrorHandling: typeof testModalErrorHandling;
    stopModalDebug?: () => void;
  }
}

// Export to global window for console access
if (typeof window !== "undefined") {
  window.testReduxModals = testReduxModals;
  window.debugModalLifecycle = debugModalLifecycle;
  window.testModalErrorHandling = testModalErrorHandling;
}

export { testReduxModals, debugModalLifecycle, testModalErrorHandling };
